import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Controller } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';

/**
 * StyledNavigationSwiper - Standalone styled-components navigation component
 *
 * Identical in style and functionality to NavigationSwiper/CompactSelectionList,
 * but works on all devices (not mobile-only) and uses styled-components.
 */

// Menu item button - styled like .link from BrandMenu
const StyledButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: var(--highlight);
  font-family: var(--heading-font);
  font-weight: 600;
  font-size: 0.8125rem;
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--transition);
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  position: relative;

  &:hover,
  &:focus {
    color: var(--highlight);
  }

  ${props => props.$active && css`
    color: var(--highlight);

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 14px;
      right: 14px;
      height: 2px;
      background: var(--highlight);
      border-radius: 2px;
    }
  `}

  @media screen and (min-width: 1024px) {
    padding: 18px 24px;
    font-size: 0.875rem;

    ${props => props.$active && css`
      border-bottom-color: var(--highlight);

      &::after {
        display: none;
      }
    `}
  }
`;

// Main container - matches CompactSelectionList exactly
const StyledContainer = styled.div`
  display: flex;
  gap: 20px;
  box-shadow: var(--widget-shadow);
  background: var(--widget);
  position: relative;
  border-radius: 4px 4px 0 0;
  overflow: hidden;

  /* Swiper container with horizontal padding to prevent overlap with nav buttons */
  .swiper {
    width: 100%;
    height: 100%;
    padding: 0 32px;
  }

  .swiper-slide {
    width: fit-content !important;
  }

  @media screen and (min-width: 1024px) {
    .swiper {
      padding: 0 50px;
    }
  }
`;

// Navigation overlay
const NavOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none;
`;

// Navigation buttons - invisible but functional for swipe area protection
const NavButton = styled.button<{ $position: 'prev' | 'next' }>`
  width: 32px;
  height: 100%;
  display: flex;
  align-items: center;
  color: transparent;
  background: transparent;
  border: none;
  cursor: default;
  padding: 0;
  pointer-events: none;

  ${props => props.$position === 'prev' && css`
    padding-left: 10px;
    justify-content: flex-start;
    border-radius: 4px 0 0 0;
  `}

  ${props => props.$position === 'next' && css`
    padding-right: 10px;
    justify-content: flex-end;
    border-radius: 0 4px 0 0;
  `}

  @media screen and (min-width: 1024px) {
    width: 50px;
  }
`;

// Chevron icons - hidden but kept for potential future use
const ChevronIcon = styled.i`
  display: none;
`;

export interface SelectionOption {
  label: string;
  value: string | number;
}

export interface StyledNavigationSwiperProps {
  active: string | number;
  setActive: (value: string | number) => void;
  options: SelectionOption[];
  innerRef?: React.RefObject<HTMLDivElement>;
  /** Additional className for custom styling */
  className?: string;
  /** Swiper instance to control (for two-way sync with content swiper) */
  controlledSwiper?: SwiperType | null;
  /** Callback to expose this component's swiper instance */
  onSwiper?: (swiper: SwiperType) => void;
}

// Re-export types for convenience
export type { SelectionOption as NavigationOption };
export type { Swiper as SwiperType } from 'swiper';

/**
 * StyledNavigationSwiper - Navigation tab component for all devices
 *
 * Features:
 * - Identical styling to NavigationSwiper/CompactSelectionList
 * - Works on desktop and mobile (no mobile-only restriction)
 * - Uses styled-components for dynamic theming
 * - Supports Swiper Controller for two-way sync with content swiper
 */
export const StyledNavigationSwiper: React.FC<StyledNavigationSwiperProps> = ({
  active,
  setActive,
  options,
  innerRef,
  className,
  controlledSwiper,
  onSwiper
}) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  // Snapshot initial active index so the very first render lands on the correct slide
  // (no slideTo animation from index 0 → active on cold load / refresh).
  const initialSlideRef = useRef(Math.max(0, options.findIndex(opt => opt.value === active)));

  // Expose swiper instance to parent
  const handleSwiper = (swiperInstance: SwiperType) => {
    setSwiper(swiperInstance);
    onSwiper?.(swiperInstance);
  };

  const handlePrev = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  // Sync swiper position when active changes externally
  useEffect(() => {
    if (swiper && active !== undefined) {
      const activeIndex = options.findIndex(opt => opt.value === active);
      if (activeIndex !== -1 && swiper.activeIndex !== activeIndex) {
        swiper.slideTo(activeIndex, 300);
      }
    }
  }, [swiper, active, options]);

  return (
    <StyledContainer ref={innerRef} className={className}>
      <NavOverlay>
        <NavButton
          $position="prev"
          aria-label="Previous"
          onClick={handlePrev}
          type="button"
        >
          <ChevronIcon />
        </NavButton>
        <NavButton
          $position="next"
          aria-label="Next"
          onClick={handleNext}
          type="button"
        >
          <ChevronIcon />
        </NavButton>
      </NavOverlay>
      <Swiper
        className="swiper"
        modules={[Controller]}
        onSwiper={handleSwiper}
        controller={controlledSwiper ? { control: controlledSwiper } : undefined}
        slidesPerView="auto"
        spaceBetween={0}
        centeredSlides={true}
        centeredSlidesBounds={true}
        initialSlide={initialSlideRef.current}
      >
        {options.map(option => (
          <SwiperSlide key={option.value} className="swiper-slide">
            <StyledButton
              $active={active === option.value}
              onClick={() => setActive(option.value)}
              type="button"
              aria-current={active === option.value ? 'page' : undefined}
              data-nav-value={option.value}
            >
              {option.label}
            </StyledButton>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledContainer>
  );
};

export default StyledNavigationSwiper;
